'use client'
import { BREAKPOINTS } from '@/constants'
import { IMG_PLACEHOLDER } from '@/constants/ui'
import { IListPromotionItem } from '@/features/coupon/types'
import { AddToCartButton, useReviewsSumary } from '@/features/products'
import { useProductDetailV2State } from '@/features/products/contexts/product-detail'
import { StyledPagination } from '@/features/products/components/product-detail/list-images-review'
import { PriceDisplay } from '@/features/products/components/product-detail/price-display'
import { SelectQuantity } from '@/features/products/components/product-detail/select-quantity'
import { useWindowSize } from '@/hooks/use-window-size'
import { useVoucherStore } from '@/store/voucher'
import StarIcon from '@mui/icons-material/Star'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  styled,
  SxProps,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import maxBy from 'lodash/maxBy'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useQueryState } from 'nuqs'
import { useMemo } from 'react'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ListOptions } from '../../stickybar-bottom'
import { VoucherItem } from '../../voucher-item'

const CommonButton = styled(Button)(({ theme }) => ({
  padding: '8px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  borderRadius: '8px',
  border: '1px solid #BBBBBA',
  [theme.breakpoints.down('md')]: {
    padding: '8px 12px',
    fontSize: 12,
  },
})) as typeof Button

interface Props {
  sx?: SxProps
  data: Product
}
export const ContentFixedProductV2 = ({ sx, data }: Props) => {
  const t = useTranslations()
  const { handleOpen, product, productSelected, quantity, productAddToCart } = useProductDetailV2State()
  const { activeVoucher, setActiveVoucher, list, isCheckAll, applyAllVoucher } = useVoucherStore()
  const [color] = useQueryState('color')
  const { dataCartPriceRule } = useVoucherStore()
  // Tính toán ảnh theo màu được chọn
  const currentImage = useMemo(() => {
    if (!product?.variants?.length) return product?.media_gallery?.[0]?.url

    let variantWithMostImages: IVariantsProduct | undefined
    if (!color) {
      // Nếu không có color trong query, lấy biến thể đầu tiên
      variantWithMostImages = maxBy(product?.variants, (v) => v?.product?.media_gallery?.length ?? 0)
    } else {
      // Tìm danh sách sản phẩm cùng màu
      const relatedProducts = product?.variants?.filter((variant) => {
        const colorAttr = variant?.attributes?.find((attr) => attr.code === 'color')
        return colorAttr?.uid === color
      })
      // Tìm biến thể có danh sách ảnh media_gallery dài nhất
      variantWithMostImages = maxBy(relatedProducts, (v) => v?.product?.media_gallery?.length ?? 0)
    }

    return variantWithMostImages?.product?.media_gallery?.[0]?.url || product?.media_gallery?.[0]?.url
  }, [color, product?.variants, product?.media_gallery])

  const { reviewSummary } = useReviewsSumary(data?.id || 0)

  const totalRating = reviewSummary?.avg_rating || 0
  const totalReviews = reviewSummary?.total_review || 0
  const { width } = useWindowSize()
  const isShowRating = width > BREAKPOINTS.xl

  const isDisableAll = list?.filter((item) => !!item?.code)?.length < 1
  const RenderListPromotion = () => {
    if (!list || list?.length === 0) return null
    return (
      <>
        <Divider sx={{ width: '1px', height: 'auto', background: '#BBBBBA' }} />

        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 300,
            alignSelf: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
          className="dot-voucher-white"
        >
          {' '}
          <Box>
            <FormControlLabel
              disabled={isDisableAll}
              sx={{
                marginLeft: '0px',
              }}
              control={
                <Checkbox checked={isCheckAll} sx={{ padding: '0px', marginRight: '8px' }} size="small" />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {t('Áp dụng tất cả khuyến mãi')}
                </Typography>
              }
              onChange={() => applyAllVoucher()}
            />
          </Box>
          <Box
            sx={{
              minWidth: 0,
              '& .swiper-wrapper': {
                alignItems: 'stretch',
              },
              '& .swiper-slide': {
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <Swiper
              freeMode={true}
              modules={[Pagination]}
              pagination={{
                clickable: true,
                type: 'bullets',
                el: '.swiper-pagination-list-voucher',
              }}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                1500: {
                  slidesPerView: 1.4,
                },
                1700: {
                  slidesPerView: 1.5,
                },
              }}
              spaceBetween={8}
            >
              {list?.map((item) => {
                const isActive = !!getTheActiveVouchers(item, activeVoucher)
                return (
                  <SwiperSlide
                    key={item?.code + item?.from_date + item?.to_date + item?.rule_id}
                    style={{ display: 'flex' }}
                  >
                    <VoucherItem
                      key={item?.code}
                      item={item}
                      isActive={isActive}
                      toggleVoucher={() => setActiveVoucher(item)}
                      expiredTextProps={{
                        position: 'right',
                        props: {
                          sx: {
                            justifyContent: 'space-between',
                            display: 'flex',
                            '& .MuiDivider-root': {
                              display: 'none',
                            },
                            '.text': {
                              display: 'none',
                            },
                          },
                        },
                      }}
                      props={{
                        sx: {
                          margin: 0,
                          // padding: { xs: '0 12px', xl: '6px 12px', xxl: '8px 16px' },
                          padding: 0,
                        },
                      }}
                      type="checkbox"
                      checkboxProps={{
                        sx: { padding: 0 },
                        size: 'small',
                      }}
                      contentDetailProps={{
                        sx: {
                          padding: '0px 0px 0px 6px',
                        },
                      }}
                      preContentProps={{
                        sx: {
                          fontSize: '10px',
                          position: { xs: 'absolute', xxl: 'static' },
                          top: '0px',
                          left: '0px',
                          transform: { xs: 'translateY(-100%)', xxl: 'unset' },
                        },
                      }}
                      blueTagProps={{
                        sx: {
                          display: 'inline-flex',
                          fontSize: { xs: '0.55rem', md: '0.65rem' },
                          p: '2px 6px',
                        },
                      }}
                    />
                  </SwiperSlide>
                )
              })}
              <StyledPagination className="swiper-pagination-list-voucher"></StyledPagination>
            </Swiper>
          </Box>
        </Box>
      </>
    )
  }

  const getTheActiveVouchers = (item: IListPromotionItem, activeVoucher: IListPromotionItem[]) => {
    if (item?.code == null) {
      return item?.is_applied
    }
    return !!activeVoucher.find((voucher) => voucher?.code === item?.code)
  }

  return (
    <Box
      key="product-content"
      component={motion.div}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      sx={{
        background: 'white',
        padding: '1rem 0',
        width: '100%',
        position: 'relative',
        maxWidth: '100%',
        transition: 'all 0.2s ease-in-out',
        display: { lg: 'block', xs: 'none' },
        ...sx,
      }}
    >
      <Stack direction={'row'} alignItems={'stretch'} gap={2}>
        {/* Information product */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '30%',
          }}
        >
          <Stack alignSelf={'flex-start'} direction={'row'} spacing={2} alignItems={'center'}>
            <Box
              sx={{
                width: { xxl: '90px', md: '92px', xs: '72px' },
                aspectRatio: '1/1',
                flexShrink: 0,
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {currentImage && (
                <Image
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  src={currentImage || IMG_PLACEHOLDER}
                  alt={product?.name || ''}
                  width={100}
                  height={100}
                />
              )}
            </Box>

            <Stack>
              {/* <Stack mb={1} spacing={1} direction={'row'} alignItems="center" sx={{ color: '#F04438' }}>
                <LocalIcon.Cup sx={{ fontSize: '1rem', color: 'inherit' }} />
                <Typography color="inherit" variant="body2" fontWeight={600} textTransform={'uppercase'}>
                  {t('Top 1 sản phẩm bán chạy')}
                </Typography>
              </Stack> */}
              {/* Product Title */}
              <Stack gap={0.5}>
                <Typography
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.375,
                  }}
                  fontWeight={600}
                  fontSize={{ xs: '0.875rem', md: '1rem' }}
                >
                  {product?.name}
                </Typography>
                <PriceDisplay
                  productSelected={productSelected}
                  product={product}
                  dataCartPriceRule={dataCartPriceRule}
                  textPriceProps={{ fontSize: '1.25rem', color: 'five.main' }}
                  textSalePriceProps={{ fontSize: '0.8rem' }}
                />

                {/* <IconButton aria-label="add to wishlist">
                <Icon.HeartV2 sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, color: 'primary.main' }} />
              </IconButton> */}
              </Stack>
              <Stack direction={'row'} spacing={0.5} alignItems="center">
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ fontSize: { xs: '0.75rem', md: '1.125rem' } }}
                >
                  <Typography fontWeight={600} fontSize="inherit">
                    {totalRating ? totalRating.toFixed(1) : 5}
                  </Typography>
                  <StarIcon sx={{ color: '#FEB50D', fontSize: { xs: '1rem', md: '1.25rem' } }} />
                </Stack>
                <Typography
                  sx={{
                    flexGrow: 1,
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    textTransform: 'lowercase',
                    color: '#c1c1c1',
                    fontWeight: 400,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => {
                    const reviewSection = document.getElementById('review')
                    if (reviewSection) {
                      window.scrollTo({
                        top: reviewSection?.offsetTop + 80,
                        behavior: 'smooth',
                      })
                    }
                  }}
                >
                  ({totalReviews} {t('Product.reviews')})
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* rating tablet */}
          {/* {!isShowRating && (
            <Stack direction="row" alignItems={'center'} gap={1} sx={{ minWidth: '105px' }}>
              <RatingBox reviewsData={reviewsData} />
              <Typography variant="body2" fontWeight={300}>
                ({reviewsData?.total_count || 0} {t('Product.detail.reviews')})
              </Typography>
              <Link href={'#review'}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: '4px', xxl: '8px' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {t('Tất cả đánh giá')}
                  </Box>
                  <Icon.ArrowRight sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} />
                </Typography>
              </Link>
            </Stack>
          )} */}
        </Box>

        {/* rating */}
        {/* {isShowRating && (
          <>
            <Divider sx={{ width: '1px', height: 'auto', background: '#BBBBBA' }} />
            <Stack gap={1} sx={{ minWidth: '105px' }}>
              <RatingBox reviewsData={reviewsData} />
              <RatingCustom value={reviewsData?.average_rating || 0} />
              <Typography variant="body2" fontWeight={300}>
                ({reviewsData?.total_count || 0} {t('Product.detail.reviews')})
              </Typography>
              <Link href={'#review'}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: '4px', xxl: '8px' },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {t('Tất cả đánh giá')}
                  </Box>
                  <Icon.ArrowRight
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, display: { xs: 'none', xl: 'block' } }}
                  />
                </Typography>
              </Link>
            </Stack>
          </>
        )} */}

        {/* List voucher */}
        {/* <RenderListPromotion /> */}
        <Divider sx={{ width: '1px', height: 'auto', background: '#BBBBBA' }} />
        <Box flex={1}>
          <ListOptions sx={{ flexDirection: 'row', gap: 3 }} />
        </Box>

        <Divider sx={{ width: '1px', height: 'auto', background: '#BBBBBA' }} />
        {/* action btn */}
        <Stack sx={{ display: { xs: 'none', lg: 'flex' }, width: '480px' }} gap={1}>
          <Stack spacing={2} flexWrap={'wrap'} justifyContent={'space-between'}>
            <SelectQuantity
              props={{
                sx: {
                  '.select-quantity-wrapper': {
                    flexDirection: 'row',
                  },
                },
              }}
              tagProps={{ sx: { transform: 'translateX(50%)', top: '-6px' } }}
              priceProps={{
                display: 'none',
                sx: {
                  '& .main-price': {
                    // fontSize: { xs: '1.25rem', xl: '1.5rem' },
                  },
                },
              }}
            />
          </Stack>
          {/* <ListComboProduct /> */}

          {/* <DiscountText
            iconProps={{
              sx: {
                fontSize: { xs: '0.875rem', xl: '1rem' },
                color: 'inherit',
                marginTop: { xs: '2px', sm: 0 },
              },
            }}
            props={{
              sx: { fontSize: { md: '12px', xl: '14px' }, color: '#F04438' },
              alignItems: { xs: 'flex-start', sm: 'center' },
            }}
          /> */}

          {/* <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ flex: 1 }}>
            <CommonButton
              // onClick={handleShowPopupSelectOption}
              onClick={() => {
                window.scrollTo(0, 0)
              }}
              sx={{
                flex: 1,
                width: '100%',
                gap: '4px',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'inherit',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                Chọn size, màu và chốt deal ngay
              </Typography>
            </CommonButton>
          </Stack> */}
          <Stack spacing={1} direction={'row'} width={'100%'}>
            <AddToCartButton
              variant="outlined"
              quantity={quantity}
              productSelected={productAddToCart}
              isSmall={true}
            />
            <AddToCartButton
              variant="contained"
              quantity={quantity}
              title="Thanh toán ngay"
              productSelected={productAddToCart}
              isBuyNow
              isSmall={true}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

// const RatingBox = ({ reviewsData }: { reviewsData: IListReview | null | undefined }) => {
//   return (
//     <Stack
//       direction="row"
//       spacing={0.5}
//       alignItems="center"
//       sx={{
//         fontSize: { xs: '1rem', md: '1.25rem' },
//         color: 'primary.contrastText',
//         px: 1,
//         backgroundColor: 'primary.main',
//         width: 'fit-content',
//         borderRadius: '4px',
//       }}
//     >
//       <Typography fontWeight={600} fontSize="inherit">
//         {(reviewsData?.average_rating || 5)?.toFixed(1)}
//       </Typography>
//       <StarIcon fontSize="inherit" sx={{ color: '#FEB50D' }} />
//     </Stack>
//   )
// }
